import { ID, Profile, Reward, Connection, OrderLedger, List } from '@omneo/omneo-sdk'
import { OmneoProfileClient } from './profileClient.js'

/**
 * @typedef {import("../types/omneo.js").OmneoCustomer} OmneoCustomer
 * @typedef {import("../types/omneo.js").OmneoConnection} OmneoConnection
 * @typedef {import("../types/omneo.js").OmneoResponse} OmneoResponse
 * @typedef {import("../types/omneo.js").OmneoCallResponse} OmneoCallResponse
 * @typedef {'DETAILS' | 'AVATAR' | 'SPORTS' | 'BRANDS' | 'SUBMIT'} onBoardingSteps
 * @typedef {{id: string, first_name: string, last_name, dob: string, tag: string}} onBoardingDetailsDTO
 * @typedef {{id: string, avatar: string}} onBoardingAvatarDTO
 * @typedef {{id: string, sport: string}} onBoardingSportDTO
 * @typedef {{idp: string, brand: string}} onBoardingBrandDTO
 * @typedef {{'DETAILS': onBoardingDetailsDTO, 'AVATAR': onBoardingAvatarDTO, 'SPORTS' : onBoardingSportDTO, 'BRANDS' : onBoardingBrandDTO, 'SUBMIT': {id: string}}} onBoardingStepsData
 */

export class OmneoClientWrapper {
  /** @param {OmneoClient} client */
  constructor(client) {
    /** @type {ID} */
    this.client = client?.id
    /** @type {OmneoProfileClient} */
    this.profileClient = new OmneoProfileClient(client) // The parent profile client
  }

  /**
   *
   * @param {string[]} brands
   */
  async updateFavouriteBrands(child, brands) {
    await child.updateCustomAttribute('omneo', 'brands', {
      value: brands,
      type: 'array'
    })
  }

  /**
   * @param {Profile} child
   * @param {string[]} sports
   */
  async updateFavouriteSports(child, sports) {
    await child.updateCustomAttribute('omneo', 'sports', {
      value: sports,
      type: 'array'
    })
  }

  async updateProfile(profile) {
    try {
      await this.client.profile.update(profile)
    } catch (err) {
      console.log(err)
    }
  }

  async updateProfileIdentity(identityId, body) {
    try {
      await this.client.profile.updateIdentity(identityId, body)
    } catch (e) {
      return false
    }

    return true
  }

  /** End Child Profile Methods */

  /**
   *
   * @param {number} connectionId
   * @return {Promise<OmneoCustomer>}
   */
  async getChildProfileByID(connectionId) {
    try {
      const connection = this.client.profile.Connection(connectionId)
      const connectionProfile = await connection.get()
      return connectionProfile
    } catch (err) {
      console.error(err)
    }
  }

  async getWishlists() {
    return await this.client.profile.getLists({ 'filter[handle]': 'wishlist' })
  }

  /**
   * @param { {name: string}} createWishlistDTO
   */
  async createWishlist(createWishlistDTO) {
    try {
      return await this.client.profile.createList({
        name: createWishlistDTO.name
      })
    } catch (err) {
      return { ...err }
    }
  }

  /**
   * @param {number} listId
   * @param {number} id
   */
  async removeWishlistItem(listId, id) {
    try {
      await this.client.profile.deleteListItem(listId, id)
    } catch (err) {
      console.log(err)
    }
  }

  async updateWishlistItem(listId, listItemId, body) {
    try {
      await this.client.profile.updateListItem(listId, listItemId, body)
    } catch (err) {
      console.log(err)
    }
  }

  /**
   * @param { {id: number, sku: string, meta: {selected: string, img: string}}} createWishlistItemDTO
   */
  async addWishlistItem(id, sku, meta) {
    try {
      return await this.client.profile.createListItem(id, {
        product_variant_sku: sku,
        meta
      })
    } catch (err) {
      console.log(err)
      return {}
    }
  }

  /**
   * Lists are used to store items for a profile. Lists are used for 2 things:
   * - Assigning shoes (use 'assigned' as the handle, default)
   * - Store wishlist items (use 'wishlist' as the handle)
   * @param {any} profile
   * @param {string} handleKey
   * @returns {Promise<List | null>}
   */
  async getOrCreateList(profile, handleKey) {
    const profileClient = profile || this.client.profile
    const handle = handleKey ?? 'assigned'
    const payload = { name: handle, handle }
    let list

    if (handle === 'wishlist') {
      list = await profileClient.getLists({ 'filter[handle][in]': 'wishlist,assigned' })
    } else {
      list = await profileClient.getLists({ 'filter[handle]': handle })
    }

    if (!list.length) {
      const newList = profileClient.createList(payload)
      return newList
    }

    const selectedList = list.find((list) => list.handle === handle)
    if (!selectedList) return null
    return selectedList
  }

  /**
   * Mutates the current profile of the client
   * @param {number} connectionId
   * @returns
   */
  async connectionProfile(connectionId) {
    const profile = this.client.profile.Connection(connectionId)
    await profile.get()
    return profile
  }

  /**
   * This function assumes you have already run the profile connection to the
   * desired child profile.
   */
  async assignItemsToChild(unassignedTransactionItemIDs, productVariantOmneoIds, connectionId, suppressRefresh = false) {
    /** Get or create list for child profile. */
    /** Get client */
    let profile = this.client.profile
    /** If the connection Id is provided fetch that new profile */
    if (connectionId) {
      profile = await this.connectionProfile(connectionId)
    }

    const list = await this.getOrCreateList(profile)
    if (!list) {
      throw new Error('Something went wrong.')
    }

    const listID = list.id
    const listItemIDs = []
    // Create the list items
    await Promise.all(productVariantOmneoIds.map(async (productVariantOmneoId, index) => {
      const listItemID = await profile.createListItem(listID, { product_variant_id: productVariantOmneoId })
      listItemIDs[index] = listItemID
    }))

    // Assign the item to the list
    await Promise.all(unassignedTransactionItemIDs.map(async (unassignedTransactionItemID, index) => {
      await this.profileClient.linkTransactionToList(unassignedTransactionItemID, {
        product_list_item_id: listItemIDs[index].id,
        type: 'link'
      })
    }))

    if (!suppressRefresh) {
      this.triggerRewardsRefresh()
    }
  }

  triggerRewardsRefresh() {
    const rewardsRefreshEvent = new CustomEvent('omneo:refresh-balance')
    window.dispatchEvent(rewardsRefreshEvent)
  }

  /**
   * This function assumes you have already run the profile connection to the
   * desired child profile.
   */
  async assignItemToChild(unassignedTransactionItemID, productVariantOmneoId, connectionId, suppressRefresh = false) {
    /** Get or create list for child profile. */
    /** Get client */
    let profile = this.client.profile
    /** If the connection Id is provided fetch that new profile */
    if (connectionId) {
      profile = await this.connectionProfile(connectionId)
    }

    const list = await this.getOrCreateList(profile)
    if (!list) {
      throw new Error('Something went wrong.')
    }

    const listID = list.id
    // Create the list item
    const listItemID = await profile.createListItem(listID, { product_variant_id: productVariantOmneoId })

    // Assign the item to the list
    await this.profileClient.linkTransactionToList(unassignedTransactionItemID, {
      product_list_item_id: listItemID.id,
      type: 'link'
    })

    if (!suppressRefresh) {
      this.triggerRewardsRefresh()
    }
  }

  /** List Data Methods End */
}
